import api from '@api';
import type { MenuGroupItem, MenuItem } from '@components/layout/sidebar/Menu';
import Menu from '@components/layout/sidebar/Menu';
import { Log } from '@enums';
import { useClickOutside, useWindowSize } from '@hooks';
import BinIcon from '@icons/bin.svg?react';
import CloseIcon from '@icons/close.svg?react';
import GwiLogo from '@icons/gwi-logo.svg?react';
import NewTabIcon from '@icons/new-tab.svg?react';
import PlusIcon from '@icons/plus.svg?react';
import RecentIcon from '@icons/recent.svg?react';
import SidebarExpandIcon from '@icons/sidebar-expand.svg?react';
import SparksIcon from '@icons/sparks.svg?react';
import { NavItem, Select } from '@staging-design-system';
import { useChatStore } from '@store/chat';
import { useProfileStore } from '@store/profile';
import { useShellStore } from '@store/shell';
import {
  AccountSettingsIcon,
  GwiDotIcon,
  MenuIcon,
  SignOutIcon
} from '@ui/icons';
import * as utils from '@utils';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatListItem } from 'types/schema';
import Help from './Help';
import Styled from './Sidebar.styled';

function Sidebar() {
  const chatStore = useChatStore();
  const shellStore = useShellStore();
  const feat = useProfileStore((state) => state.feat);
  const user = useProfileStore((state) => state.user);
  const [agentVersions, setAgentVersions] = useState<string[]>();
  const [mobileDrawerIsOpen, setMobileDrawerIsOpen] = useState(false);
  const [mobileAccountDrawerIsOpen, setMobileAccountDrawerIsOpen] =
    useState(false);
  const mobileShellRef = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();

  const newChatPath = shellStore.pathname || '/';
  const isMobile = windowSize.isSmall;
  const isExpanded = isMobile || shellStore.isSidebarExpanded;

  const { accountInitial, accountName } = useMemo(() => {
    const firstName = user?.first_name ?? '';
    const lastName = user?.last_name ?? '';
    const fullName = `${firstName} ${lastName}`.trim();

    return {
      accountInitial: firstName[0] ?? '',
      accountName: fullName || 'GWI User'
    };
  }, [user]);

  const startChat = useCallback(() => {
    setMobileDrawerIsOpen(false);
    setMobileAccountDrawerIsOpen(false);

    chatStore.clear();
    shellStore.reset();
    utils.pushPath(newChatPath);
  }, [chatStore.clear, shellStore.reset, newChatPath]);

  const openChat = useCallback(
    async (chatId: string) => {
      chatStore.setQuestionPanelData([]);
      shellStore.setIsQuestionPanelOpen(false);
      chatStore.setSessionIsLoading(true);

      try {
        const chat = await api.chats.getById(chatId);
        const transformedChat = {
          ...chat,
          title: chat.title,
          messages: chat.messages.map((item) => ({
            ...item.message,
            metadata: {
              ...item.message.metadata,
              ['from_saved_chat']: true
            }
          }))
        };
        // React will batch these store updates
        // and trigger only one re-rendering
        chatStore.setCurrentChat(transformedChat, true);

        const questions = utils.findPendingQuestionPanel(
          transformedChat.messages
        );

        if (questions) {
          chatStore.setQuestionPanelData(questions);
          shellStore.setIsQuestionPanelOpen(true);
        }
      } catch (_22) {
        shellStore.setToast({
          message: 'Sorry, something went wrong.',
          status: 'error'
        });
        utils.pushPath(shellStore.pathname);
      } finally {
        chatStore.setSessionIsLoading(false);
      }
    },
    [
      chatStore.setCurrentChat,
      chatStore.setQuestionPanelData,
      chatStore.setSessionIsLoading,
      shellStore.setIsQuestionPanelOpen,
      shellStore.setToast,
      shellStore.pathname
    ]
  );

  const handleChangedPathname = useCallback(() => {
    const chatId = utils.getPathnameId();

    if (chatId) {
      openChat(chatId);
      utils.log('Opened chat session using the current URL.', chatId);
    } else if (
      !utils.isSettingsPath(window.location.pathname) &&
      window.location.pathname !== shellStore.pathname
    ) {
      // Change pathname to default
      utils.replacePath(shellStore.pathname);
    }
  }, [openChat, shellStore.pathname]);

  const handleSelectChatSession = useCallback(
    (item: ChatListItem, event?: MouseEvent<HTMLElement>) => {
      event?.stopPropagation();
      setMobileDrawerIsOpen(false);
      setMobileAccountDrawerIsOpen(false);

      if (shellStore.isRightPanelOpen) {
        shellStore.setIsRightPanelOpen(false);
      }

      utils.log(`Opened chat '${item.title}'.`, item.id);

      api.analytics.openedRecentChat({
        place: 'side_panel',
        ['chat_id']: item.id
      });

      // Update URL in address bar
      utils.pushPath(`${shellStore.pathname}/chat/${item.id}`);

      // Render the selected chat session
      openChat(item.id);
    },
    [
      openChat,
      shellStore.isRightPanelOpen,
      shellStore.pathname,
      shellStore.setIsRightPanelOpen
    ]
  );

  const handleClickDeleteAction = useCallback(
    async (item: MenuGroupItem, deleteItem?: () => void) => {
      await api.chats.deleteById(item.id);

      // Hide item from the menu list
      deleteItem?.();

      if (item.active) {
        utils.log('Deleted current chat.', item.id);
        // Deleting currernt chat will take user back to intro view
        utils.pushPath(shellStore.pathname);
        chatStore.clear();
      } else {
        utils.log(`Deleted chat "${item.title}"`, item.id);
      }
    },
    [chatStore.clear, shellStore.pathname]
  );

  const actionList: MenuGroupItem['actionList'] = useMemo(
    () => [
      {
        label: 'Open in new tab',
        value: 'new-tab',
        selected: false,
        startIcon: <NewTabIcon />,
        onClick: (groupItem) =>
          window.open(`${shellStore.pathname}/chat/${groupItem.id}`, '_blank')
      },
      {
        label: 'Delete',
        value: 'delete',
        selected: false,
        startIcon: <BinIcon />,
        onClick: (groupItem, deleteItem) =>
          handleClickDeleteAction(
            {
              ...groupItem,
              active: groupItem.id === chatStore.chat.id
            },
            deleteItem
          )
      }
    ],
    [chatStore.chat.id, shellStore.pathname, handleClickDeleteAction]
  );

  // Avoid unnecessary re-mappings
  const memoizedChatList = useMemo(
    () =>
      chatStore.history.map(({ title, chats }) => ({
        title,
        items: chats?.map((item) => ({
          ...item,
          title: utils.formatChatTitle(item),
          actionList
        }))
      })),
    [actionList, chatStore.history]
  );

  const handleSelectAgentVersion = useCallback(
    (item: ChatListItem, event?: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      chatStore.setAgentVersion(item.title);
      window.localStorage.setItem('octopus.agent', item.title);
      utils.log(`Agent version changed to '${item.title}'.`);
    },
    [chatStore.setAgentVersion]
  );

  const handleClickMenuItem = useCallback(() => {
    shellStore.setIsSidebarExpanded(true);
  }, [shellStore.setIsSidebarExpanded]);

  const handleToggleMobileDrawer = useCallback(() => {
    setMobileDrawerIsOpen((isOpen) => {
      const nextIsOpen = !isOpen;

      if (nextIsOpen) {
        setMobileAccountDrawerIsOpen(false);
      }

      return nextIsOpen;
    });
  }, []);

  const handleToggleMobileAccountDrawer = useCallback(() => {
    setMobileAccountDrawerIsOpen((isOpen) => {
      const nextIsOpen = !isOpen;

      if (nextIsOpen) {
        setMobileDrawerIsOpen(false);
      }

      return nextIsOpen;
    });
  }, []);

  const handleOpenSettings = useCallback(() => {
    setMobileAccountDrawerIsOpen(false);
    utils.pushPath('/settings');
  }, []);

  const handleLogOut = useCallback(() => {
    setMobileAccountDrawerIsOpen(false);
    utils.logOut();
  }, []);

  const accountOptions = useMemo(
    () => [
      {
        startIcon: <AccountSettingsIcon />,
        label: 'Account settings',
        value: 'settings',
        onClick: handleOpenSettings
      },
      {
        startIcon: <SignOutIcon />,
        label: 'Log out',
        value: 'log_out',
        onClick: handleLogOut
      }
    ],
    [handleOpenSettings, handleLogOut]
  );

  const closeMobileDrawers = useCallback(() => {
    setMobileDrawerIsOpen(false);
    setMobileAccountDrawerIsOpen(false);
  }, []);

  const handleLazyLoading = useCallback(async () => {
    utils.log('Lazy loading triggered.', Log.Notice);

    try {
      // Lazy loading of the chat list
      const page = api.chats.pageGenerator.next();
      const olderChats = await api.chats.getAll(page.value);

      // Check if the chat list is empty
      const isEmptyList = olderChats
        .map(({ chats }) => chats)
        .every((item) => item.length === 0);

      if (!isEmptyList) {
        chatStore.setHistory((history) => {
          const augmentedHistory = [...history].map((item, index) => ({
            ...item,
            chats: [...item.chats, ...olderChats[index].chats]
          }));

          return augmentedHistory;
        });
      }

      return { hasMore: !isEmptyList };
    } catch (_error) {
      shellStore.setToast({
        message: 'Sorry, something went wrong.',
        status: 'error'
      });

      return { hasMore: false };
    }
  }, [shellStore.setToast, chatStore.setHistory]);

  const memoizedMenuItems: MenuItem<MenuGroupItem>[] = useMemo(
    () => [
      {
        icon: <SparksIcon className="icon" />,
        hidden: !feat.agentVersion.enabled,
        label: 'Agent Versions',
        groups: agentVersions
          ? [
              {
                items: agentVersions.map((agentName) => ({
                  title: agentName,
                  id: agentName
                }))
              }
            ]
          : [],
        activeGroupItemId: chatStore.agentVersion,
        onClickGroupItem: handleSelectAgentVersion
      },
      {
        active: true,
        icon: <RecentIcon className="icon" />,
        hidden: memoizedChatList.length === 0,
        label: 'Recents',
        groups: memoizedChatList,
        activeGroupItemId: chatStore.chat.id,
        pathname: `${shellStore.pathname}/chat`,
        onClickGroupItem: handleSelectChatSession,
        onLazyLoading: handleLazyLoading
      }
    ],
    [
      agentVersions,
      feat.agentVersion.enabled,
      memoizedChatList,
      chatStore.chat.id,
      chatStore.agentVersion,
      shellStore.pathname,
      handleSelectChatSession,
      handleSelectAgentVersion,
      handleLazyLoading
    ]
  );

  useEffect(() => {
    handleChangedPathname();

    const handlePopState = () => {
      // Notice, this wouldn't work using 'history.state' as
      // this would be persistent to history changes.
      if (sessionStorage.getItem('octopus.popstate_event.ignore_duplicate')) {
        sessionStorage.removeItem('octopus.popstate_event.ignore_duplicate');
        utils.log(
          'Navigation was blocked due to a popstate event' +
            ' ' +
            'to avoid unintended history changes.',
          Log.Warning
        );
        return;
      }

      handleChangedPathname();
    };

    // Capture the URL changes
    window.addEventListener('popstate', handlePopState);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handleChangedPathname]);

  useEffect(() => {
    if (feat.agentVersion.enabled) {
      // Retrieve all available agents
      api.user.getAgentVersions().then((agentVersions) => {
        const agentVersion = utils.getStorageItem('octopus.agent');

        // Set current agent version
        if (agentVersion) {
          chatStore.setAgentVersion(agentVersion);
        } else if (agentVersions?.length) {
          const [defaultAgentVersion] = agentVersions;
          chatStore.setAgentVersion(defaultAgentVersion);
          window.localStorage.setItem('octopus.agent', defaultAgentVersion);
        }

        setAgentVersions(agentVersions);
      });
    }
  }, [chatStore.setAgentVersion, feat.agentVersion.enabled]);

  useEffect(() => {
    // Retrieve recent chat list from the server
    api.chats.getAll().then(chatStore.setHistory);
  }, [chatStore.setHistory]);

  useEffect(() => {
    if (!isMobile) {
      setMobileDrawerIsOpen(false);
      setMobileAccountDrawerIsOpen(false);
    }
  }, [isMobile]);

  useClickOutside(
    mobileShellRef,
    isMobile && (mobileDrawerIsOpen || mobileAccountDrawerIsOpen),
    closeMobileDrawers
  );

  const main = (
    <>
      <Styled.NavButtonGroup isExpanded={isExpanded}>
        <NavItem
          data-testid="new-chat"
          disabled={chatStore.isLoading || chatStore.sessionIsLoading}
          icon={<PlusIcon />}
          label="New chat"
          linkHref={newChatPath}
          selected={
            !chatStore.hasStartedChat() &&
            window.location.pathname === newChatPath
          }
          showLabel={isExpanded}
          type="button"
          onClick={startChat}
        />
      </Styled.NavButtonGroup>
      <Menu
        isExpanded={isExpanded}
        items={memoizedMenuItems}
        onClickMenuItem={handleClickMenuItem}
      />
    </>
  );

  if (isMobile) {
    return (
      <Styled.MobileShell ref={mobileShellRef}>
        <Styled.MobileHeader>
          <Styled.MobileHeaderGroup>
            <Styled.MobileHeaderButton
              type="button"
              onClick={handleToggleMobileDrawer}>
              {mobileDrawerIsOpen ? <CloseIcon /> : <MenuIcon />}
            </Styled.MobileHeaderButton>
            <Styled.MobileLogo>
              <GwiLogo />
            </Styled.MobileLogo>
          </Styled.MobileHeaderGroup>
          <Styled.MobileAvatarButton
            type="button"
            aria-expanded={mobileAccountDrawerIsOpen}
            aria-label="Open account menu"
            onClick={handleToggleMobileAccountDrawer}>
            <Styled.AccountAvatar>
              <GwiDotIcon />
              <Styled.AccountAvatarInitial>
                {accountInitial}
              </Styled.AccountAvatarInitial>
            </Styled.AccountAvatar>
          </Styled.MobileAvatarButton>
        </Styled.MobileHeader>
        <Styled.MobileDrawer
          id="mobile-sidebar-drawer"
          isBusy={chatStore.isLoading}
          isOpen={mobileDrawerIsOpen}
          data-testid="sidebar-container">
          {main}
          <Styled.MobileFooter>
            <Help variant="compact" onActionComplete={closeMobileDrawers} />
          </Styled.MobileFooter>
        </Styled.MobileDrawer>
        <Styled.MobileDrawer
          id="mobile-account-drawer"
          anchor="right"
          isBusy={chatStore.isLoading}
          isOpen={mobileAccountDrawerIsOpen}>
          <Styled.MobileAccountContent>
            {accountOptions.map((item) => (
              <NavItem
                key={item.value}
                icon={item.startIcon}
                label={item.label}
                showLabel={isExpanded}
                type="button"
                onClick={item.onClick}
              />
            ))}
          </Styled.MobileAccountContent>
        </Styled.MobileDrawer>
      </Styled.MobileShell>
    );
  }

  return (
    <Styled.Sidebar
      isBusy={chatStore.isLoading}
      isExpanded={shellStore.isSidebarExpanded}
      tabIndex={0}
      data-testid="sidebar-container">
      <Styled.Header isExpanded={shellStore.isSidebarExpanded}>
        <Styled.Logo isExpanded={shellStore.isSidebarExpanded}>
          <GwiLogo />
        </Styled.Logo>
        <Styled.HeaderToggle
          type="button"
          data-testid="sidebar-collapse"
          onClick={() =>
            shellStore.setIsSidebarExpanded(!shellStore.isSidebarExpanded)
          }>
          <SidebarExpandIcon />
        </Styled.HeaderToggle>
      </Styled.Header>
      {main}
      <Styled.Footer isExpanded={shellStore.isSidebarExpanded}>
        <Help variant="dropdown" />
        <Select
          icon={
            <Styled.AccountAvatar>
              <GwiDotIcon />
              <Styled.AccountAvatarInitial>
                {accountInitial}
              </Styled.AccountAvatarInitial>
            </Styled.AccountAvatar>
          }
          label={accountName}
          options={accountOptions}
          placement="top"
          showLabel={shellStore.isSidebarExpanded}
        />
      </Styled.Footer>
    </Styled.Sidebar>
  );
}

export default Sidebar;
